import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bltDecisionSelect } from "../../../redux/selector/bltDecisionSelector"
import {
    BLTNavigationTop,
    BLTScreenNavigation
} from '../BLTCommon/BLTNavigationTop'
import '../BLTHome/BLTHome.styles.scss'
import '../../../components/ReviewForm/BLTReviewForm.styles.scss'
import { Button, ErrorMark } from '../../../components/RegularComponents'
import DocumentViewer from '../../../components/DocumentViewer/DocumentViewer'
import ApplicationStatus from '../BLTCommon/ApplicationStatus'
import * as CONST from '../../../app/constants'
import { DocumentViewerFileNameLink } from '../../../components/DocumentViewer/DocumentFileComponent';
import { fullName, humanReadableDate, isBranchApplication } from '../../../app/utils'
import { fetchBusinessLicnecePDF, sendEmail } from '../../../redux/slice/bltDecisionSlice'
import { ERROR_STRING } from '../../../app/errors'
import { push } from 'connected-react-router'
import { resetQueue } from '../../../redux/slice/bltHomeSlice'
import { convertFileDataForUrl } from '../../../app/documentUploadUtils'
import { 
    applicationTypeFromLicenceSubType,
    isAgencyTypeApplication, 
    isCollectionTypeApplication, 
    isIndividualTypeApplication, 
    isMortgageTypeApplication, 
    isRenewalTypeApplication, 
    titleStringFromLicenceSubType 
} from '../../../redux/slice/applicationsUtils'

// Added env variables in branch for MRA-1506 - pending BL side approval to merge
const EMAIL_MORTGAGE = process.env.REACT_APP_DECISON_SUMMARY_MORTGAGE_REPLY_EMAIL || "mortgageregulation@novascotia.ca"

const EMAIL_COLLECTION = process.env.REACT_APP_DECISON_SUMMARY_COLLECTION_REPLY_EMAIL || "reglicensing@novascotia.ca"

function BLTDecisionSummary(props) {
    
    const {bltDecision} = props
    const {queueItem, primaryApplication, currentAgentApplication, currentBranchApplication} = bltDecision
    const [subjectText, setSubjectText] = useState("")
    const [messageText, setMessageText] = useState("")
    const [validationError, setValidationError] = useState(false)
    const [errorString, setErrorString] = useState("")
    const [messageRequestStatus, setMessageRequestStatus] = useState(CONST.REQUEST_STATUS.IDLE)

    const [licencePDF, setLicencePDF] = useState(null);
    const [openDocumentViewer, setOpenDocumentViewer] = useState(false)
    const [documentViewerDocument, setDocumentViewerDocument] = useState(null)

    let application = null;

    if(isAgencyTypeApplication(queueItem)) {
        if(isBranchApplication(queueItem)) {
            application = currentBranchApplication
        } else {
            application = primaryApplication
        }
    } else {
        application = currentAgentApplication
    }

    useEffect(() => {
        const fetchPDF = async () => {
            const pdf = await fetchBusinessLicnecePDF(queueItem.id);
            setLicencePDF(pdf);
        } 
        fetchPDF();
    }, [queueItem])

    const mainTitle = () => {
        let header = ""
        const { applicationStatus, licenceStatus } = application

        let t1 = ""
        const sep = " - "

        const isRenewal = isRenewalTypeApplication(application)

        if (applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED) {
            t1 = `Approved${isRenewal ? ' renewal ' : ' '}application`
        } else if (applicationStatus === CONST.APPLICATION_STATUS_DENIED) {
            t1 = `Denied${isRenewal ? ' renewal ' : ' '}application`
        } else if (applicationStatus === CONST.APPLICATION_STATUS_PENDING) {
            t1 = `Pending${isRenewal ? ' renewal ' : ' '}application`
        } else if (applicationStatus === CONST.APPLICATION_STATUS_WITHDRAWN_BY_CLIENT) {
            t1 = `Cancelled by client`
        } else if(applicationStatus === CONST.APPLICATION_STATUS_WITHDRAWN_APPROVED) {
            /* If "Cancelled by client" selected from adjudication screen then It goes to APPLICATION_STATUS_WITHDRAWN_APPROVED
            instead of APPLICATION_STATUS_WITHDRAWN_BY_CLIENT (To avoid confirm button in pending queue list) */
            t1 = licenceStatus === (CONST.LICENCE_STATUS_WITHDRAWN_BY_CLIENT ? `Cancelled by client` : `Cancelled`) + isRenewal ? ' renewal' : ''
        }

        let t2 = titleStringFromLicenceSubType(queueItem.licencingSubType)

        if (isBranchApplication(queueItem)) {
            t2 = 'Additional Branch'
        }

        if (t2) {
            header = [t1, t2].join(sep)
        } else {
            header = t1
        }

        return header
    }

    const typeOfContactString = useCallback(() => {
        const applicationType = applicationTypeFromLicenceSubType(queueItem.licencingSubType)
        if(applicationType === CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE || 
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE ||
            applicationType === CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_LICENCE ||
            applicationType === CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_LICENCE){
                return "Compliance Officer"
            }
        else if(isMortgageTypeApplication(applicationType)){
             return "Principal Broker"
        } else {
            return "Business Contact"
        }
    },[queueItem])

    const contactPersonName = () => {
        const applicationType = applicationTypeFromLicenceSubType(queueItem.licencingSubType)
        if((isMortgageTypeApplication(applicationType) || 
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE || 
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE) && 
            primaryApplication?.businessLicenceData?.personData) {
            return fullName(primaryApplication.businessLicenceData.personData)
        }

        if((isIndividualTypeApplication(queueItem) || isBranchApplication(queueItem)) && 
            primaryApplication?.businessLicenceData?.mainBusinessContact) { 
            return fullName(primaryApplication.businessLicenceData.mainBusinessContact)
        }

        return fullName(queueItem.businessLicenceData?.mainBusinessContact)
    }

    const contactPersonEmail = () => {
        const applicationType = applicationTypeFromLicenceSubType(queueItem.licencingSubType)

        if((isMortgageTypeApplication(applicationType) || 
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE ||
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE) && 
            primaryApplication?.businessLicenceData?.personData?.email) {
            return primaryApplication.businessLicenceData.personData.email
        }

        if((isIndividualTypeApplication(queueItem) || isBranchApplication(queueItem)) && 
            primaryApplication?.businessLicenceData?.mainBusinessContact?.emailAddress) { 
            return primaryApplication.businessLicenceData.mainBusinessContact.emailAddress
        }

        return queueItem.businessLicenceData?.mainBusinessContact?.emailAddress ?? ""
    }

    // Also used for replyEmail
    const getCCEmail = () => {
        const applicationType = applicationTypeFromLicenceSubType(queueItem.licencingSubType);

        if(isCollectionTypeApplication(applicationType)) {
            return EMAIL_COLLECTION;
        }
        else {
            //For isLenderTypeApplication, isAdministratorTypeApplication, isMortgageTypeApplication,
            // isMortgageMainAgentTypeApplication, isIndividaulPrincipalBrokerApplication
            return EMAIL_MORTGAGE;
        }
       
    }


    const mediumTitle = useCallback(() => {
        return application?.businessLicenceData?.businessName ?? ""
    }, [application])

    const legalNameTitle = () => {
        return application?.businessLicenceData?.companyRjscDetails?.businessData?.businessOwner ?? ""
    }

    const subTitle = useCallback(() => {

        const appData = application.businessLicenceData

        let title = ""

        if(isAgencyTypeApplication(queueItem) && !isBranchApplication(queueItem)) { 
            title = fullName(appData?.mainBusinessContact)
            title += `, ${typeOfContactString()}`
        } else if(isIndividualTypeApplication(queueItem)) { 
            title = fullName(appData?.personData)
        }

        return title
    },[queueItem, application,typeOfContactString])

    const bodyText = () => { 
        const {applicationStatus} = application
        if(applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED) { 
            return (<p>{`Licence information below will be sent to the ${typeOfContactString()}`}</p>)
        } else {
            return (<p>Use this form to contact the applicant and get the information you need for their application</p>)
        }
    }

    const applicationDetails = () => {
        let approveData = {}

        const appData = application.businessLicenceData
        const {applicationStatus} = application

        if(applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED
            && !isBranchApplication(queueItem)) {
            approveData = {
                "Licence number" : application.licenceNumber ? application.licenceNumber : "N/A",
                "PDF Copy" : appData.businessName + "_Licence.pdf",
                "Issued on" : application.issuedDate ? humanReadableDate(application.issuedDate) : (appData.startDate ? humanReadableDate(appData.startDate) : ""),
                "Expires on" : application.endDate ? humanReadableDate(application.endDate) : "N/A",
            }
        } 

        if(isBranchApplication(application) && appData.additionalBranches?.length === 1) {
            const branchData = appData.additionalBranches[0]

            approveData = {
                ...approveData,
                "Branch name" : branchData.branchName,
                "Responsible agent" : fullName(branchData.manager),
                "Responsible agent email" : branchData.manager.emailAddress
            }
        } else {
            approveData = {
                ...approveData,
                [typeOfContactString()] : contactPersonName(),
                "Email address" : contactPersonEmail(),

            }
        }

        approveData = Object.defineProperty(approveData,
            `${(applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED) ? "Message" : "Email"} to applicant`,
            {
                value : "",
                writable: true,
                enumerable: true
            })

        let elements = []

        for (const key in approveData) {
            if (Object.hasOwnProperty.call(approveData, key)) {
                const value = approveData[key];
                const componentId = key.toLowerCase().replace(/ +/g, '')

                if (key === "Message to applicant"
                    || key === "Email to applicant") {
                    elements.push(
                        <div key={"data-review-" + componentId} className="row bottomMarginNormal bltReviewFormItem">
                            <div className={"col-lg-10 bottomMarginNormal"}>
                                <b>{key}</b>
                            </div>
                            <div className={"col-lg-7"}>
                                <textarea
                                    id={componentId + "-input"}
                                    rows={4}
                                    value={messageText}
                                    onInput={e => setMessageText(e.target.value)}>
                                </textarea>
                            </div>
                        </div>
                    )
                } else if (key === "Subject") {
                    elements.push(
                        <div key={"data-review-" + componentId} className="row bottomMarginNormal bltReviewFormItem noBorder">
                            <div className={"col-lg-10 bottomMarginNormal"}>
                                <b>{key}</b>
                            </div>
                            <div className={"col-lg-7"}>
                                <input
                                    className="ns-input fw-20"
                                    type="text"
                                    id={componentId + "-text-input"}
                                    name={componentId + "-text-input"}
                                    onInput={e => setSubjectText(e.target.value)}
                                    value={subjectText} />
                            </div>
                        </div>
                    )
                }
                else {
                    elements.push(
                        <div key={"data-review-" + componentId} className="row bottomMarginNormal bltReviewFormItem">
                            <div className={"col-lg-3"}>
                                <b>{key}</b>
                            </div>
                            <div className={"col-lg-7"}>
                                {key === "PDF Copy" ?
                                    <DocumentViewerFileNameLink
                                        id={"supporting-document-view-file-" + key}
                                        key={"supporting-document-view-file-" + key}
                                        file={licencePDF ?? {}}
                                        onClick={() => {
                                            let fileDataUrl = convertFileDataForUrl(licencePDF.file, CONST.DOCUMENT_CONTENT_TYPE_PDF)
                                            setDocumentViewerDocument(fileDataUrl)
                                            setOpenDocumentViewer(true)  
                                        }}
                                    /> : value }
                            </div>
                        </div>
                    )
                }
            }
        }

        return (
            <>
                {elements}
                <DocumentViewer 
                    fileData={documentViewerDocument}
                    fileName={licencePDF?.filename}
                    isOpen={openDocumentViewer}
                    handleClose={() => setOpenDocumentViewer(false)}
                />
            </>
        )
    }

    const handleSendMessage = () => {

        if(messageRequestStatus === CONST.REQUEST_STATUS.IN_PROGRESS)
            return

        if(!messageText) {
            setValidationError(true)
            setErrorString(ERROR_STRING.BLT_DECISION_NO_MESSAGE)
            return
        }

        const contactEmail = contactPersonEmail()
        if(!contactEmail) { 
            setValidationError(true)
            setErrorString(ERROR_STRING.BLT_DECISION_NO_EMAIL)
            return
        }
        
        setValidationError(false);

        const formattedMessageText = messageText.replace(/\n/g, "<br />")

        const payloadData = {
            toEmail : [contactEmail],
            subject : subjectText ? subjectText : mainTitle(),
            body : formattedMessageText,
            fromEmail: getCCEmail() //Sender
        }

        if(application.applicationStatus !== CONST.APPLICATION_STATUS_APPROVED_ISSUED) {
            //For status other that approved, add the CC email 
            payloadData.cc = [getCCEmail()];
        }
        
        setMessageRequestStatus(CONST.REQUEST_STATUS.IN_PROGRESS)
        props.dispatch(sendEmail(payloadData))
        .then(d => {
            setMessageRequestStatus(CONST.REQUEST_STATUS.SUCCESS)
        })
        .catch(err => {
            setMessageRequestStatus(CONST.REQUEST_STATUS.ERROR)
            setValidationError(true)
            setErrorString(ERROR_STRING.BLT_REVIEW_DECISION_MESSAGE_DELIVERY_FAIURE)
        })
        .finally(() => {
            props.dispatch(resetQueue())
            props.dispatch(push(CONST.ROUTE_PATH.BLT_HOME))
        })
    }

    useEffect(() => {
        const {applicationStatus} = application
        const isRenewal = isRenewalTypeApplication(application)
        let primaryBusinessApplicationType = CONST.APPLICATION_TYPE_COLLECTION_AGENCY
        let applicationString = `Your ${titleStringFromLicenceSubType(queueItem.licencingSubType).toLowerCase()}${isRenewal ? ' renewal' : ''} application is approved`

        if (isBranchApplication(queueItem)) {
            applicationString = `Your additional branch application is approved`
        }

        switch(queueItem.licencingSubType) {
            case CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION :
            case CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_RENEWAL :
                if(isRenewal)
                    applicationString = "Your company is approved"
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_COLLECTION_AGENCY
                break
            case CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_APPLICATION :
            case CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_RENEWAL :
                if(isRenewal)
                    applicationString = "Your company is approved"
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_DEBT_MANAGEMENT_AGENCY
                break
            case CONST.LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_APPLICATION :
                // The link for collector application should still 
                // say "collection-agency" becuase the dashboard is visible
                // only for the agency application
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_COLLECTION_AGENCY
                break
            case CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_APPLICATION :
                // The link for debt-managemen- agent application should still 
                // say "debt-management-agency" becuase the dashboard is visible
                // only for the agency application
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_DEBT_MANAGEMENT_AGENCY
                break
            case CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_NEW_APPLICATION :
            case CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION :
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE
                if(isRenewal)
                    applicationString =  "Your Mortgage Broker licence renewal application is approved."
                else 
                    applicationString =  "Your Mortgage Broker licence application is approved."
                break
            case CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_NEW_APPLICATION : 
            case CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_NEW_APPLICATION : 
            case CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_NEW_APPLICATION :
            case CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_RENEWAL_APPLICATION : 
            case CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION : 
            case CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION :
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE
                break
            case CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_NEW_APPLICATION : 
            case CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_RENEWAL_APPLICATION :
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE
                break
            case CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_NEW_APPLICATION :
            case CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_RENEWAL_APPLICATION :
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE
                break
            case CONST.LICENCE_SUB_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_APPLICATION:
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_LICENCE;
                applicationString = `Your ${titleStringFromLicenceSubType(queueItem.licencingSubType).toLowerCase()} is approved`
                break;
            case CONST.LICENCE_SUB_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_RETENTION_APPLICATION:
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_RETENTION_LICENCE;
                applicationString = `Your ${titleStringFromLicenceSubType(queueItem.licencingSubType).toLowerCase()} is approved`
                break;
            case CONST.LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_APPLICATION:
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_LICENCE;
                applicationString = `Your ${titleStringFromLicenceSubType(queueItem.licencingSubType).toLowerCase()} is approved`
                break;
            case CONST.LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_APPLICATION:
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_LICENCE;
                applicationString = `Your ${titleStringFromLicenceSubType(queueItem.licencingSubType).toLowerCase()} is approved`
                break;
            default : 
                primaryBusinessApplicationType = CONST.APPLICATION_TYPE_COLLECTION_AGENCY
                break
        }
        
        if(applicationStatus === CONST.APPLICATION_STATUS_APPROVED_ISSUED) {
            const serverUrl = `${window.location.protocol}//${window.location.host}/#`
            let appParams = `/?applicationType=${primaryBusinessApplicationType}&applicationMode=${CONST.APPLICATION_MODE_NEW}`
            
            if(primaryBusinessApplicationType === CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE) {
                appParams = `${CONST.ROUTE_PATH.MRA_APP}${CONST.ROUTE_PATH.MRA_APPLY_FOR_MORTGAGE_LICENCE}`
            } else if(primaryBusinessApplicationType === CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE) {
                appParams = `${CONST.ROUTE_PATH.ML_APP}${CONST.ROUTE_PATH.ML_APPLY_LENDER_LICENCE}`
            } else if(primaryBusinessApplicationType === CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE) {
                appParams = `${CONST.ROUTE_PATH.MA_APP}${CONST.ROUTE_PATH.MA_APPLY_ADMIN_LICENCE}`
            }
            
            const link = `${serverUrl}${appParams}`
            
            setMessageText(`${applicationString}. To view your licence and print a copy, sign in to the Business Licensing service : \n\n ${link}`)
        } else if(applicationStatus === CONST.APPLICATION_STATUS_WITHDRAWN_APPROVED) {
            /*  This message is to confirm that a licence for [Name of holder]was withdrawn by [Business name] on [Date].
                If you have any questions, please contact business licensing at 902-424-5200 or toll free at 1-800-670-4357. 
             */
            
            const text = `This message is to confirm that a licence for ${subTitle()} was withdrawn by ${mediumTitle()} on ${humanReadableDate(Date.now())}. \n\nIf you have any questions, please contact business licensing at 902-424-5200 or toll free at 1-800-670-4357.`
            setMessageText(text)
        }
    }, [queueItem, setMessageText, application, subTitle, mediumTitle])

    if(!application) {
        return null;
    }

    return (
        <>
            <BLTNavigationTop />
            <div className={"container"}>
                <BLTScreenNavigation {...props} />
                <div className="row bottomMarginMedium">
                    <div className="col-lg-12">
                        <h1>{mainTitle()}</h1>
                    </div>
                </div>
                <div className="row bottomMarginMedium">
                    <div className="col-lg-8">
                        <div>
                            <span className="bltReview-businessName">
                                {mediumTitle()}
                            </span>
                        </div>
                        <div>
                            <span className="bltReview-legalName">
                                {legalNameTitle()}
                            </span>
                        </div>
                        <div>
                            <span className="bltReview-contactPersonName">
                                {subTitle()}
                            </span>
                        </div>
                    </div>
                    <div className="col-lg-2">
                        <div className="row">
                            <h4>Current Status</h4>
                        </div>
                        <div className="row">
                            <ApplicationStatus applicationStatus={application.applicationStatus} licenceStatus={application.licenceStatus}/>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-8">
                        {bodyText()}
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-10 decisionReviewForm">
                        {applicationDetails()}
                    </div>
                </div>

                <div className="row">
                    <div className={"col-lg-6" + (validationError ? " has-error" : "")}>
                        <Button
                            showSpinner={messageRequestStatus === CONST.REQUEST_STATUS.IN_PROGRESS}
                            buttonType="regular_primary"
                            text="Send"
                            onClick={handleSendMessage}
                            id="blt-decision-summary-send-message"
                        />
                        {validationError && ErrorMark()}
                        {validationError && <div className="help-block">
                            {errorString}
                        </div>}
                    </div>

                </div>
            </div>
        </>
    )
}

export default connect(bltDecisionSelect)(BLTDecisionSummary)
